import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import {
	Shop,
	ShopDeals,
	ShopStateFilter,
	Shops,
	SubscriptionHistory,
	UserThemes,
} from '../model/types';

const SHOPS = `/api/admin/shops`;

export const SHOPS_KEY = 'shops';

/** Сколько магазинов на странице списка. */
export const SHOPS_PAGE_SIZE = 20;
/** Сколько сделок показываем в карточке магазина. */
export const SHOP_DEALS_PAGE_SIZE = 20;

interface ShopsQueryParams {
	name: string;
	state: ShopStateFilter | null;
	page: number;
	/** Только магазины одного владельца — для карточки пользователя. */
	ownerUuid?: string;
}

export const useGetShops = ({
	name,
	state,
	page,
	ownerUuid,
}: ShopsQueryParams) => {
	return useQuery<Shops>(
		[SHOPS_KEY, name, state, page, ownerUuid],
		async () => {
			const { data } = await axios.get(`${SHOPS}/`, {
				params: {
					offset: (page - 1) * SHOPS_PAGE_SIZE,
					limit: SHOPS_PAGE_SIZE,
					...(name ? { name } : {}),
					...(state ? { state } : {}),
					...(ownerUuid ? { owner_uuid: ownerUuid } : {}),
				},
			});
			return data;
		},
		{
			refetchOnWindowFocus: false,
			// Прошлая страница остаётся на экране, пока грузится следующая:
			// иначе таблица схлопывается в спиннер на каждое нажатие.
			keepPreviousData: true,
		}
	);
};

export const useGetShop = (uuid: string | undefined) => {
	return useQuery<Shop>(
		[SHOPS_KEY, 'card', uuid],
		async () => {
			const { data } = await axios.get(`${SHOPS}/${uuid}`);
			return data;
		},
		{
			enabled: Boolean(uuid),
			refetchOnWindowFocus: false,
		}
	);
};

/**
 * Включить или выключить магазин.
 *
 * Инвалидируется весь раздел, а не одна карточка: состояние магазина видно
 * и в списке, и иначе оператор вернулся бы к списку, где магазин ещё
 * «Работает».
 */
export const useSetShopActive = (uuid: string | undefined) => {
	const queryClient = useQueryClient();

	return useMutation<Shop, unknown, boolean>(
		async active => {
			const action = active ? 'activate' : 'deactivate';
			const { data } = await axios.patch(`${SHOPS}/${uuid}/${action}`);
			return data;
		},
		{
			onSuccess: () => queryClient.invalidateQueries(SHOPS_KEY),
		}
	);
};

export const useGetShopDeals = (uuid: string | undefined, page: number) => {
	return useQuery<ShopDeals>(
		[SHOPS_KEY, 'deals', uuid, page],
		async () => {
			const { data } = await axios.get(`${SHOPS}/${uuid}/deals`, {
				params: {
					offset: (page - 1) * SHOP_DEALS_PAGE_SIZE,
					limit: SHOP_DEALS_PAGE_SIZE,
				},
			});
			return data;
		},
		{
			enabled: Boolean(uuid),
			refetchOnWindowFocus: false,
			keepPreviousData: true,
		}
	);
};

const USERS = `/api/admin/users`;

/** История оплаченных периодов. Продление заводит новую подписку, а не
 *  продлевает старую, поэтому их у продавца накапливается цепочка. */
export const useGetSubscriptionHistory = (userUuid: string | undefined) => {
	return useQuery<SubscriptionHistory>(
		[SHOPS_KEY, 'subscriptions', userUuid],
		async () => {
			const { data } = await axios.get(
				`${USERS}/${userUuid}/mini-app/subscriptions`
			);
			return data;
		},
		{
			enabled: Boolean(userUuid),
			refetchOnWindowFocus: false,
		}
	);
};

/** Собственные темы продавца — их не больше четырёх. */
export const useGetUserThemes = (userUuid: string | undefined) => {
	return useQuery<UserThemes>(
		[SHOPS_KEY, 'themes', userUuid],
		async () => {
			const { data } = await axios.get(`${USERS}/${userUuid}/mini-app/themes`);
			return data;
		},
		{
			enabled: Boolean(userUuid),
			refetchOnWindowFocus: false,
		}
	);
};
